import React, { useCallback, useEffect, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'

import Header from '../../../components/Headers/Header'
import { api } from '../../../utils/index'
import { extractData, notify, prefix } from '../../../utils/index_tagging_tool'
import { getDimensionGroups } from '../../api_methods'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import EmptyTranslations from '../TranslationUtility/EmptyTranslations'
import AiDimensionLinking from './AiDimensionLinking'
import CloneReport from './CloneReport'
import DimensionLinking from './DimensionLinking'
import MetricLinking from './MetricLinking'
import SeoConfiguration from './SeoConfiguration'
import SeoPreview from './SeoPreview'
import UnsavedRules from './UnsavedRules/UnsavedRules'

const SeoReport = (props) => {
  const cardWidth = 9
  const [reportId] = useState(props.match.params.id)
  const [report, setReport] = useState(null)
  const [savedDimensions, setSavedDimensions] = useState([])
  const [dimensionGroups, setDimensionGroups] = useState([])
  const [savedMetrics, setSavedMetrics] = useState([])
  const [running, setRunning] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const loadReport = useCallback(() => {
    return api
      .get(`/tagging-tool/reports/${reportId}/`)
      .then(extractData)
      .then((data) => {
        setReport(data)
        const groupsDimensions = data.groups.reduce((accumulator, currentGroup) => {
          return accumulator.concat(currentGroup.dimensions)
        }, [])
        setSavedDimensions(groupsDimensions)
      })
      .catch(console.error)
  }, [reportId])

  useEffect(() => {
    loadReport()

    getDimensionGroups()
      .then((data) => setDimensionGroups(data))
      .catch(console.error)

    api
      .get(`/tagging-tool/saved-metrics/`)
      .then(extractData)
      .then((data) => setSavedMetrics(data))
      .catch(console.error)
  }, [loadReport])

  const updateReport = (updatedReport) => {
    setReport(updatedReport)
  }

  const runReport = async () => {
    if (report && (report.status === 'READY_TO_RUN' || report.status === 'RUNNING')) {
      notify(
        `Il report e gia in stato ${report.status}. Attendi il completamento della run corrente prima di avviarne un'altra.`,
        'error'
      )
      return
    }

    setRunning(true)

    api
      .post(prefix + `reports/${reportId}/run/`, { credentials: 'include' })
      .then(extractData)
      .then((data) => {
        if (data === 'Report running') {
          notify('La run del report è partita correttamente', 'success')
        } else if (data === 'Report already running') {
          notify('Il report è già stato avviato da qualcun altro.', 'error')
        } else {
          notify('Il report è andato in errore, contattare un innovation.', 'error')
        }
      })
      .catch((error) => {
        notify(
          error?.response?.data?.error || "Impossibile avviare il report in questo momento.",
          'error'
        )
      })
      .finally(() => setRunning(false))
  }

  const softDeleteReport = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo report?')) {
      return
    }

    setDeleting(true)
    api
      .post(`${prefix}reports/${reportId}/soft-delete/`)
      .then(() => {
        notify('Report eliminato con successo.', 'success')
        props.history.push('/reporting/')
      })
      .catch(() => {
        notify('Impossibile eliminare il report.', 'error')
      })
      .finally(() => {
        setDeleting(false)
      })
  }

  const pageTitle = report ? `${reportId} - ${report.name} - ${report.status}` : 'Loading Report...'

  if (report === null) {
    return (
      <>
        <Row className="justify-content-center">
          <Col className="mb-20" style={{ marginBottom: '100px' }} xs={cardWidth}>
            <Card className="shadow">
              <Loading child={null} />
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <>
      <Header iconClass="fa-solid fa-tags fa-xl" title="Tagging Tool" />
      <Row style={{ marginBottom: '100px' }}>
        <Col md={10}>
          <Card className="bg-secondary shadow flex">
            <CardHeader>
              <PageTitle title={pageTitle} link={`/reporting/`} linkText={'Torna alla lista dei report'} />
            </CardHeader>
            <CardBody className="pt-0 pt-md-4">
              <Tabs defaultActiveKey="settings">
                <Tab title="Configurazione" eventKey="settings" justify>
                  <SeoConfiguration
                    reportId={report.id}
                    reportFile={report.input_options.path}
                    reportClient={report.input_options.client || ''}
                    reportLanguage={report.input_options.language_code || ''}
                    reportLingua={report.input_options.language || ''}
                    reportKw={report.input_options.kw_column}
                    reportVol={report.input_options.vol_column}
                    reportOutput={report.output_options}
                    reportName={report.name}
                    reportGroups={report.groups}
                    groupOptions={dimensionGroups.map((dimensionGroup) => {
                      return { label: dimensionGroup.name, value: dimensionGroup.name }
                    })}
                    updateReport={updateReport}
                    seo={report.seo}
                  />
                </Tab>
                <Tab title="Dimensioni" eventKey="dimensions">
                  <DimensionLinking
                    dimensions={report.dimensions}
                    savedDimensions={savedDimensions}
                    reportName={report.name}
                    seo={true}
                  />
                </Tab>
                <Tab title="Dimensioni AI ✨" eventKey="aiDimensions">
                  <AiDimensionLinking
                    report={report}
                    aiDimensions={report.ai_dimensions || []}
                    onReload={loadReport}
                  />
                </Tab>
                <Tab title="Metriche" eventKey="metrics">
                  <MetricLinking
                    metrics={report.custom_metrics}
                    savedMetrics={savedMetrics}
                    reportName={report.name}
                    seo={true}
                  />
                </Tab>
                <Tab title="Stato del report" eventKey="status">
                  <p className="mb-3 mt-3">
                    In questa sezione sono presenti funzionalità per osservare lo stato di alcune caratteristiche del
                    report. All&apos;interno ogni sottosezione si potrà trovare una spiegazione dettagliata.
                  </p>
                  <EmptyTranslations identifier={report.id} info={report} group={false} />
                  <UnsavedRules identifier={report.id} info={report} />
                </Tab>
                <Tab title="Simulazione" eventKey="simulation">
                  <SeoPreview
                    reportId={report.id}
                    reportName={report.name}
                    dimensions={report.dimensions}
                    ignoredColumns={report.input_options.ignore_for_simulation}
                  />
                </Tab>
              </Tabs>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="bg-secondary shadow flex">
            <CardBody>
              <Row className="justify-content-center">
                <CloneReport id={reportId} />
              </Row>
              <Row className="justify-content-center mt-3">
                {running ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  <Button
                    color="primary"
                    disabled={report.status === 'READY_TO_RUN' || report.status === 'RUNNING'}
                    onClick={() => runReport()}
                    block
                  >
                    <i className="fas fa-upload"></i> Salva su BigQuery
                  </Button>
                )}
              </Row>
              <Row className="justify-content-center mt-3">
                {deleting ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                ) : (
                  <Button color="danger" outline onClick={() => softDeleteReport()} block>
                    Elimina
                  </Button>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default SeoReport
